/**
 * How a casino dresses its tables. One owner setting, read by every screen
 * that plays there — the TV, the cast receiver and the phones. Today it is
 * the felt, the chips and the deck; rails join this object as the house grows
 * a look of its own.
 */

/** The house felts, in the order the console offers them. */
export const FELT_IDS = [
  'emerald',
  'burgundy',
  'crimson',
  'mahogany',
  'tobacco',
  'moss',
  'teal',
  'royal',
  'slate',
  'midnight',
  'indigo',
  'plum',
  'magenta',
  'charcoal',
  'hacker',
] as const;

export type FeltId = (typeof FELT_IDS)[number];

/**
 * One felt as three shades: `deep` at the table's edge, `base` across it and
 * `light` under the lamp. Each preset is tuned dark enough that the gold and
 * cream printed on it stay readable. `deepRgb` is `deep` as an `r, g, b`
 * triplet, for the translucent washes that sit over the felt.
 */
export interface FeltShades {
  label: string;
  deep: string;
  base: string;
  light: string;
  deepRgb: string;
}

export const FELTS: Record<FeltId, FeltShades> = {
  emerald: { label: 'Emerald', deep: '#06231a', base: '#0d3d2b', light: '#14563b', deepRgb: '6, 35, 26' },
  burgundy: { label: 'Burgundy', deep: '#23060b', base: '#3d0d16', light: '#5a1623', deepRgb: '35, 6, 11' },
  royal: { label: 'Royal blue', deep: '#061429', base: '#0d2447', light: '#173766', deepRgb: '6, 20, 41' },
  teal: { label: 'Teal', deep: '#032120', base: '#0a3a38', light: '#12544f', deepRgb: '3, 33, 32' },
  plum: { label: 'Plum', deep: '#1a0823', base: '#2e103d', light: '#45195a', deepRgb: '26, 8, 35' },
  charcoal: { label: 'Charcoal', deep: '#111315', base: '#212529', light: '#33393e', deepRgb: '17, 19, 21' },
  crimson: { label: 'Crimson', deep: '#2b0507', base: '#560c11', light: '#7a1419', deepRgb: '43, 5, 7' },
  mahogany: { label: 'Mahogany', deep: '#1e0e05', base: '#3a1d0c', light: '#552b13', deepRgb: '30, 14, 5' },
  tobacco: { label: 'Tobacco', deep: '#221604', base: '#3f2a08', light: '#5a3d0f', deepRgb: '34, 22, 4' },
  moss: { label: 'Moss', deep: '#141b06', base: '#28360e', light: '#3b4f16', deepRgb: '20, 27, 6' },
  slate: { label: 'Slate', deep: '#0d151e', base: '#1c2a38', light: '#2a3e52', deepRgb: '13, 21, 30' },
  midnight: { label: 'Midnight', deep: '#04070f', base: '#0a1122', light: '#131d36', deepRgb: '4, 7, 15' },
  indigo: { label: 'Indigo', deep: '#0d0a29', base: '#1c164d', light: '#2b2270', deepRgb: '13, 10, 41' },
  magenta: { label: 'Magenta', deep: '#27061a', base: '#4a0d31', light: '#681546', deepRgb: '39, 6, 26' },
  hacker: { label: 'Hacker', deep: '#020503', base: '#08110b', light: '#102015', deepRgb: '2, 5, 3' },
};

/** The denominations every chip rail and tray is cut from. */
export const CHIP_DENOMS = [5, 25, 100, 500] as const;

export type ChipDenom = (typeof CHIP_DENOMS)[number];

/**
 * One denomination's face: `top` where the light catches it, `rim` at its
 * edge, `ink` for the printed number and `edge` for the dashed inlay ring.
 */
export interface ChipFace {
  top: string;
  rim: string;
  ink: string;
  edge: string;
}

export interface ChipSet {
  label: string;
  faces: Record<ChipDenom, ChipFace>;
}

/** The house chip sets, in the order the console offers them. */
export const CHIP_SET_IDS = [
  'classic',
  'monaco',
  'platinum',
  'highroller',
  'jewel',
  'ocean',
  'sunset',
  'retro',
  'pastel',
  'neon',
  'recital',
  'jazz',
  'hacker',
] as const;

export type ChipSetId = (typeof CHIP_SET_IDS)[number];

const INLAY = 'rgba(255, 255, 255, 0.65)';
const GOLD_INLAY = 'rgba(232, 195, 122, 0.75)';
const TERMINAL_INLAY = 'rgba(158, 224, 122, 0.7)';

/**
 * Chips are placed by denomination, so each set keeps its four faces apart
 * at a glance. Chips already on the felt wear their player's colour instead,
 * whatever the set — that is who bet, not how much.
 */
export const CHIP_SETS: Record<ChipSetId, ChipSet> = {
  classic: {
    label: 'Classic',
    faces: {
      5: { top: '#e05545', rim: '#a02619', ink: '#ffe9e5', edge: INLAY },
      25: { top: '#3fae6a', rim: '#1c6b3c', ink: '#e2ffe9', edge: INLAY },
      100: { top: '#3d4351', rim: '#15181f', ink: '#ffd98a', edge: GOLD_INLAY },
      500: { top: '#9d5bd2', rim: '#5b2c8f', ink: '#f7e9ff', edge: INLAY },
    },
  },
  monaco: {
    label: 'Monte Carlo',
    faces: {
      5: { top: '#f3ecdc', rim: '#c8b894', ink: '#3a2a14', edge: 'rgba(122, 84, 34, 0.55)' },
      25: { top: '#c0485a', rim: '#6e1422', ink: '#ffe6ea', edge: INLAY },
      100: { top: '#4a6fb8', rim: '#172e63', ink: '#e6eeff', edge: INLAY },
      500: { top: '#f0cf7c', rim: '#a8802f', ink: '#2a1d05', edge: 'rgba(255, 255, 255, 0.75)' },
    },
  },
  platinum: {
    label: 'Platinum',
    faces: {
      5: { top: '#e3e8ec', rim: '#8f98a1', ink: '#1b2025', edge: 'rgba(0, 0, 0, 0.35)' },
      25: { top: '#8190a0', rim: '#3b4550', ink: '#eef3f7', edge: INLAY },
      100: { top: '#454545', rim: '#101010', ink: '#ffd98a', edge: GOLD_INLAY },
      500: { top: '#f0cf7c', rim: '#b08a2e', ink: '#231903', edge: 'rgba(255, 255, 255, 0.75)' },
    },
  },
  highroller: {
    label: 'High roller',
    faces: {
      5: { top: '#f5efe2', rim: '#b8ab8c', ink: '#1c1406', edge: 'rgba(122, 84, 34, 0.55)' },
      25: { top: '#c9a45c', rim: '#7e6025', ink: '#1a1204', edge: 'rgba(255, 255, 255, 0.7)' },
      100: { top: '#2a2a2e', rim: '#0a0a0c', ink: '#e8c37a', edge: GOLD_INLAY },
      500: { top: '#7a1f2b', rim: '#3d0a12', ink: '#ffd98a', edge: GOLD_INLAY },
    },
  },
  jewel: {
    label: 'Crown jewels',
    faces: {
      5: { top: '#d0324a', rim: '#7d0f22', ink: '#ffe6ea', edge: GOLD_INLAY },
      25: { top: '#2fae82', rim: '#0f6446', ink: '#e0fff2', edge: GOLD_INLAY },
      100: { top: '#3c63d6', rim: '#162f80', ink: '#e6ecff', edge: GOLD_INLAY },
      500: { top: '#a35ad8', rim: '#5a2091', ink: '#f6e9ff', edge: GOLD_INLAY },
    },
  },
  ocean: {
    label: 'Ocean',
    faces: {
      5: { top: '#bfeef2', rim: '#62b6c0', ink: '#07303a', edge: 'rgba(7, 48, 58, 0.45)' },
      25: { top: '#3cc0d0', rim: '#127885', ink: '#03262a', edge: INLAY },
      100: { top: '#2a6fb3', rim: '#0f3766', ink: '#e6f2ff', edge: INLAY },
      500: { top: '#1a2e5c', rim: '#070f26', ink: '#ffd98a', edge: GOLD_INLAY },
    },
  },
  sunset: {
    label: 'Sunset',
    faces: {
      5: { top: '#ffcf5a', rim: '#c08a14', ink: '#2e1f00', edge: INLAY },
      25: { top: '#ff8a3d', rim: '#b8480b', ink: '#2b1100', edge: INLAY },
      100: { top: '#e8436a', rim: '#8f1535', ink: '#fff0f4', edge: INLAY },
      500: { top: '#7a3fbf', rim: '#3d1a73', ink: '#f3e8ff', edge: INLAY },
    },
  },
  retro: {
    label: 'Retro clay',
    faces: {
      5: { top: '#e3b23c', rim: '#9a7212', ink: '#2a1c00', edge: 'rgba(255, 244, 214, 0.7)' },
      25: { top: '#7f9a3a', rim: '#48591a', ink: '#f4ffe0', edge: 'rgba(255, 244, 214, 0.7)' },
      100: { top: '#d0642a', rim: '#823510', ink: '#fff0e6', edge: 'rgba(255, 244, 214, 0.7)' },
      500: { top: '#6b3e22', rim: '#3a1e0d', ink: '#ffe7c7', edge: 'rgba(255, 244, 214, 0.7)' },
    },
  },
  pastel: {
    label: 'Pastel',
    faces: {
      5: { top: '#f6a6b2', rim: '#c0667a', ink: '#3a0f18', edge: INLAY },
      25: { top: '#a8e0c8', rim: '#5aa586', ink: '#0e2d20', edge: INLAY },
      100: { top: '#b5c4f5', rim: '#6a7fc4', ink: '#111c44', edge: INLAY },
      500: { top: '#f5dc9a', rim: '#c3a14e', ink: '#2e2106', edge: INLAY },
    },
  },
  neon: {
    label: 'Neon',
    faces: {
      5: { top: '#ff6fb0', rim: '#b8216a', ink: '#fff0f7', edge: INLAY },
      25: { top: '#4fe3f0', rim: '#12808c', ink: '#03262a', edge: INLAY },
      100: { top: '#a8f05a', rim: '#4f8f16', ink: '#13240a', edge: INLAY },
      500: { top: '#ffb04d', rim: '#c05f06', ink: '#2b1500', edge: INLAY },
    },
  },
  recital: {
    label: 'Recital',
    faces: {
      5: { top: '#f7f2e6', rim: '#bdb096', ink: '#141414', edge: 'rgba(20, 20, 20, 0.4)' },
      25: { top: '#d9ae4f', rim: '#8c6a1c', ink: '#1f1603', edge: 'rgba(255, 255, 255, 0.7)' },
      100: { top: '#1e1e22', rim: '#050506', ink: '#f7f2e6', edge: 'rgba(247, 242, 230, 0.6)' },
      500: { top: '#8e1f33', rim: '#480a17', ink: '#fbe9c4', edge: GOLD_INLAY },
    },
  },
  jazz: {
    label: 'Jazz club',
    faces: {
      5: { top: '#2b2640', rim: '#110e1d', ink: '#f2c14e', edge: 'rgba(242, 193, 78, 0.6)' },
      25: { top: '#16595e', rim: '#06292c', ink: '#d8fbf5', edge: 'rgba(216, 251, 245, 0.55)' },
      100: { top: '#6e1e4a', rim: '#330a21', ink: '#ffd9ec', edge: 'rgba(255, 217, 236, 0.55)' },
      500: { top: '#c98a2b', rim: '#6e4610', ink: '#1a1005', edge: 'rgba(26, 16, 5, 0.45)' },
    },
  },
  hacker: {
    label: 'Hacker',
    faces: {
      5: { top: '#1c241e', rim: '#050806', ink: '#9ee07a', edge: TERMINAL_INLAY },
      25: { top: '#2f6e3c', rim: '#123a1b', ink: '#e4ffd8', edge: TERMINAL_INLAY },
      100: { top: '#9ee07a', rim: '#4c8a33', ink: '#06110a', edge: 'rgba(6, 17, 10, 0.55)' },
      500: { top: '#e2ffd4', rim: '#8fc27a', ink: '#06110a', edge: 'rgba(47, 110, 60, 0.7)' },
    },
  },
};

/** The suits as a deck prints them, in the letters `Card.suit` uses. */
export const DECK_SUITS = ['S', 'H', 'D', 'C'] as const;

export type DeckSuit = (typeof DECK_SUITS)[number];

/** The side of the square the suit pips are drawn in. */
export const SUIT_PIP_BOX = 24;

/**
 * A suit pip as geometry, drawn in a `SUIT_PIP_BOX` square and filled with the
 * deck's ink for that suit. Every renderer draws these rather than the ♠♥♦♣
 * glyphs: the glyphs come out of whatever font the platform happens to have —
 * colour emoji on TV Chromium and older Android, the system serif on tvOS —
 * and a glyph that carries its own colour ignores the deck's suit inks
 * altogether. Geometry prints one deck on every screen.
 */
export const SUIT_PIP_PATHS: Record<DeckSuit, string> = {
  S: 'M12 1C9 5.5 2 9.5 2 14.5 2 17.6 4.3 19.5 6.8 19.5c1.9 0 3.3-1 4-2.3-.3 2.3-1.2 3.9-2.8 5.3h8c-1.6-1.4-2.5-3-2.8-5.3.7 1.3 2.1 2.3 4 2.3 2.5 0 4.8-1.9 4.8-5C22 9.5 15 5.5 12 1z',
  H: 'M12 22C9.5 19.2 2 13.6 2 8.1 2 4.9 4.4 2.5 7.2 2.5c2 0 3.8 1.1 4.8 2.9 1-1.8 2.8-2.9 4.8-2.9C19.6 2.5 22 4.9 22 8.1c0 5.5-7.5 11.1-10 13.9z',
  D: 'M12 1.5 20 12 12 22.5 4 12z',
  C: 'M12 1.5a4.9 4.9 0 0 0-4.3 7.3A4.9 4.9 0 1 0 10.9 16c-.3 2.6-1.3 4.6-3 6.5h8.2c-1.7-1.9-2.7-3.9-3-6.5a4.9 4.9 0 1 0 3.2-7.2A4.9 4.9 0 0 0 12 1.5z',
};

/** What a suit is called, for the screen readers that meet a pip. */
export const SUIT_NAMES: Record<DeckSuit, string> = {
  S: 'spades',
  H: 'hearts',
  D: 'diamonds',
  C: 'clubs',
};

/**
 * The back of every card in a deck: `ground` under a repeating `pattern`,
 * inside a `border`. `ground` may name a felt variable, so a back can take on
 * whichever cloth the venue wears.
 */
/**
 * The back's pattern as data, so a renderer without CSS can draw it. `pattern`
 * above is the same design as a CSS background; this is the same design as
 * geometry, for the native shells (they draw it in SVG). Both describe one
 * deck — `theme.test.ts` holds them to the same ink.
 *
 * Sizes are in card-back pixels at the web's natural scale.
 */
export type BackPattern =
  /** Parallel lines at `angle` degrees. */
  | { kind: 'stripes'; ink: string; angle: number; width: number; pitch: number }
  /** Stripes crossing at ±45°. */
  | { kind: 'lattice'; ink: string; width: number; pitch: number }
  /** Stripes crossing at 0° and 90°. */
  | { kind: 'grid'; ink: string; width: number; pitch: number }
  | { kind: 'dots'; ink: string; radius: number; pitch: number }
  | { kind: 'checks'; ink: string; size: number }
  /** A music staff: five lines `pitch` apart, one note head, per `tile` band. */
  | { kind: 'staff'; ink: string; width: number; pitch: number; tile: number };

export interface DeckBack {
  ground: string;
  pattern: string;
  /** The same pattern as geometry, for renderers with no CSS. */
  backPattern: BackPattern;
  border: string;
}

/**
 * The face of every card: `paper` inside an `edge` hairline, and an ink per
 * suit. A two-colour deck repeats its red and its black; a four-colour deck
 * gives each suit its own.
 */
export interface DeckFace {
  paper: string;
  edge: string;
  suits: Record<DeckSuit, string>;
}

export interface Deck {
  label: string;
  back: DeckBack;
  face: DeckFace;
}

/** The house decks, in the order the console offers them. */
export const DECK_IDS = [
  'house',
  'riverboat',
  'midnight',
  'parlour',
  'royale',
  'vintage',
  'arctic',
  'tournament',
  'harlequin',
  'noir',
  'obsidian',
  'graphite',
  'gilded',
  'oxblood',
  'ember',
  'forest',
  'abyss',
  'amethyst',
  'eclipse',
  'sheet',
  'nocturne',
  'rose',
  'neonrose',
  'neon',
  'hacker',
] as const;

export type DeckId = (typeof DECK_IDS)[number];

const lattice = (ink: string, width: string, pitch: string): string =>
  `repeating-linear-gradient(45deg, ${ink} 0 ${width}, transparent ${width} ${pitch}), ` +
  `repeating-linear-gradient(-45deg, ${ink} 0 ${width}, transparent ${width} ${pitch})`;

/**
 * A strip of manuscript: a five-line staff with an eighth note and a beamed
 * pair sitting on it, tiled down and across the back.
 */
const staff = (ink: string): string => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 24" fill="${ink}" stroke="${ink}">` +
    '<g stroke-width="0.6" opacity="0.45">' +
    [3, 6, 9, 12, 15].map((y) => `<line x1="0" x2="28" y1="${y}" y2="${y}"/>`).join('') +
    '</g>' +
    '<g stroke-width="1.1">' +
    '<ellipse cx="5" cy="12" rx="2.1" ry="1.5" transform="rotate(-20 5 12)" stroke="none"/>' +
    '<path d="M6.9 11.5V3.5c1.5 1.1 3.4 2 2.6 5" fill="none"/>' +
    '<ellipse cx="15" cy="9" rx="2.1" ry="1.5" transform="rotate(-20 15 9)" stroke="none"/>' +
    '<ellipse cx="22" cy="10.5" rx="2.1" ry="1.5" transform="rotate(-20 22 10.5)" stroke="none"/>' +
    '<path d="M16.9 8.5V1.5M23.9 10V2" fill="none"/>' +
    '<path d="M16.9 1.5L23.9 2" stroke-width="2"/>' +
    '</g></svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 0 0 / 49px 42px`;
};

/**
 * Every face stays light-on-dark or dark-on-light enough to read at TV
 * distance, and every deck keeps its reds apart from its blacks.
 */
export const DECKS: Record<DeckId, Deck> = {
  house: {
    label: 'House',
    back: {
      ground: 'var(--felt-deep)',
      pattern: 'repeating-linear-gradient(45deg, rgba(232, 195, 122, 0.35) 0 4px, transparent 4px 8px)',
      backPattern: { kind: 'stripes', ink: 'rgba(232, 195, 122, 0.35)', angle: 45, width: 4, pitch: 8 },
      border: '#e8c37a',
    },
    face: {
      paper: '#f5efe2',
      edge: 'transparent',
      suits: { S: '#08150f', H: '#b3261e', D: '#b3261e', C: '#08150f' },
    },
  },
  riverboat: {
    label: 'Riverboat',
    back: {
      ground: '#9e1b22',
      pattern: lattice('rgba(255, 255, 255, 0.28)', '2px', '7px'),
      backPattern: { kind: 'lattice', ink: 'rgba(255, 255, 255, 0.28)', width: 2, pitch: 7 },
      border: '#f7f4ee',
    },
    face: {
      paper: '#fbfaf6',
      edge: 'rgba(0, 0, 0, 0.12)',
      suits: { S: '#111111', H: '#c8102e', D: '#c8102e', C: '#111111' },
    },
  },
  midnight: {
    label: 'Midnight',
    back: {
      ground: '#0b1a3a',
      pattern: 'radial-gradient(circle, rgba(192, 206, 230, 0.45) 0 1.5px, transparent 2px) 0 0 / 7px 7px',
      backPattern: { kind: 'dots', ink: 'rgba(192, 206, 230, 0.45)', radius: 1.5, pitch: 7 },
      border: '#c0cee6',
    },
    face: {
      paper: '#f4f6fa',
      edge: 'rgba(11, 26, 58, 0.18)',
      suits: { S: '#0b1a3a', H: '#b3261e', D: '#b3261e', C: '#0b1a3a' },
    },
  },
  parlour: {
    label: 'Parlour',
    back: {
      ground: '#1f5a3a',
      pattern: lattice('rgba(245, 239, 226, 0.3)', '2px', '8px'),
      backPattern: { kind: 'lattice', ink: 'rgba(245, 239, 226, 0.3)', width: 2, pitch: 8 },
      border: '#f5efe2',
    },
    face: {
      paper: '#f8f4ea',
      edge: 'rgba(31, 90, 58, 0.2)',
      suits: { S: '#10231a', H: '#b3261e', D: '#b3261e', C: '#10231a' },
    },
  },
  royale: {
    label: 'Royale',
    back: {
      ground: '#3b1a5a',
      pattern: 'radial-gradient(circle, rgba(232, 195, 122, 0.55) 0 1.5px, transparent 2px) 0 0 / 8px 8px',
      backPattern: { kind: 'dots', ink: 'rgba(232, 195, 122, 0.55)', radius: 1.5, pitch: 8 },
      border: '#e8c37a',
    },
    face: {
      paper: '#f7f1e6',
      edge: 'rgba(59, 26, 90, 0.2)',
      suits: { S: '#1a0f24', H: '#a8123a', D: '#a8123a', C: '#1a0f24' },
    },
  },
  vintage: {
    label: 'Vintage',
    back: {
      ground: '#6b4a2b',
      pattern: 'repeating-linear-gradient(135deg, rgba(245, 225, 190, 0.25) 0 2px, transparent 2px 6px)',
      backPattern: { kind: 'stripes', ink: 'rgba(245, 225, 190, 0.25)', angle: 135, width: 2, pitch: 6 },
      border: '#efe2c4',
    },
    face: {
      paper: '#efe2c4',
      edge: 'rgba(107, 74, 43, 0.35)',
      suits: { S: '#2b1d10', H: '#9b2a1a', D: '#9b2a1a', C: '#2b1d10' },
    },
  },
  arctic: {
    label: 'Arctic',
    back: {
      ground: '#dfe9f2',
      pattern: lattice('rgba(30, 70, 120, 0.28)', '1px', '6px'),
      backPattern: { kind: 'lattice', ink: 'rgba(30, 70, 120, 0.28)', width: 1, pitch: 6 },
      border: '#1e4678',
    },
    face: {
      paper: '#ffffff',
      edge: 'rgba(30, 70, 120, 0.25)',
      suits: { S: '#1e2a3a', H: '#d02a3a', D: '#d02a3a', C: '#1e2a3a' },
    },
  },
  harlequin: {
    label: 'Harlequin',
    back: {
      ground: '#7a1426',
      pattern: 'conic-gradient(rgba(0, 0, 0, 0.35) 25%, transparent 0 50%, rgba(0, 0, 0, 0.35) 0 75%, transparent 0) 0 0 / 10px 10px',
      backPattern: { kind: 'checks', ink: 'rgba(0, 0, 0, 0.35)', size: 10 },
      border: '#f0cf7c',
    },
    face: {
      paper: '#fdfbf6',
      edge: 'rgba(0, 0, 0, 0.12)',
      suits: { S: '#111111', H: '#c8102e', D: '#e07a10', C: '#6a2bb0' },
    },
  },
  tournament: {
    label: 'Four-colour',
    back: {
      ground: '#14365e',
      pattern: 'repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.16) 0 3px, transparent 3px 9px)',
      backPattern: { kind: 'stripes', ink: 'rgba(255, 255, 255, 0.16)', angle: 90, width: 3, pitch: 9 },
      border: '#f5efe2',
    },
    face: {
      paper: '#ffffff',
      edge: 'rgba(0, 0, 0, 0.12)',
      suits: { S: '#111111', H: '#c8102e', D: '#1f5fbf', C: '#1e7a34' },
    },
  },
  noir: {
    label: 'Noir',
    back: {
      ground: '#0c0c0e',
      pattern: lattice('rgba(232, 195, 122, 0.22)', '1px', '6px'),
      backPattern: { kind: 'lattice', ink: 'rgba(232, 195, 122, 0.22)', width: 1, pitch: 6 },
      border: '#ffd98a',
    },
    face: {
      paper: '#1c1c20',
      edge: 'rgba(232, 195, 122, 0.55)',
      suits: { S: '#f5efe2', H: '#ff6b5e', D: '#ff6b5e', C: '#f5efe2' },
    },
  },
  obsidian: {
    label: 'Obsidian',
    back: {
      ground: '#08080a',
      pattern: lattice('rgba(192, 196, 204, 0.22)', '1px', '6px'),
      backPattern: { kind: 'lattice', ink: 'rgba(192, 196, 204, 0.22)', width: 1, pitch: 6 },
      border: '#c0c4cc',
    },
    face: {
      paper: '#121216',
      edge: 'rgba(192, 196, 204, 0.5)',
      suits: { S: '#e6e8ec', H: '#ff5a6e', D: '#ff5a6e', C: '#e6e8ec' },
    },
  },
  graphite: {
    label: 'Graphite',
    back: {
      ground: '#1c1f23',
      pattern:
        'repeating-linear-gradient(0deg, rgba(154, 163, 173, 0.18) 0 1px, transparent 1px 6px), ' +
        'repeating-linear-gradient(90deg, rgba(154, 163, 173, 0.18) 0 1px, transparent 1px 6px)',
      backPattern: { kind: 'grid', ink: 'rgba(154, 163, 173, 0.18)', width: 1, pitch: 6 },
      border: '#9aa3ad',
    },
    face: {
      paper: '#202428',
      edge: 'rgba(154, 163, 173, 0.45)',
      suits: { S: '#f0f2f4', H: '#ff8a80', D: '#ff8a80', C: '#f0f2f4' },
    },
  },
  gilded: {
    label: 'Gilded',
    back: {
      ground: '#0a0805',
      pattern: 'repeating-linear-gradient(45deg, rgba(240, 207, 124, 0.3) 0 2px, transparent 2px 7px)',
      backPattern: { kind: 'stripes', ink: 'rgba(240, 207, 124, 0.3)', angle: 45, width: 2, pitch: 7 },
      border: '#f0cf7c',
    },
    face: {
      paper: '#0e0b07',
      edge: 'rgba(240, 207, 124, 0.6)',
      suits: { S: '#f0cf7c', H: '#ff6b5e', D: '#ff6b5e', C: '#f0cf7c' },
    },
  },
  oxblood: {
    label: 'Oxblood',
    back: {
      ground: '#2a0409',
      pattern: 'radial-gradient(circle, rgba(232, 195, 122, 0.45) 0 1.5px, transparent 2px) 0 0 / 7px 7px',
      backPattern: { kind: 'dots', ink: 'rgba(232, 195, 122, 0.45)', radius: 1.5, pitch: 7 },
      border: '#e8c37a',
    },
    face: {
      paper: '#1a0508',
      edge: 'rgba(232, 195, 122, 0.5)',
      suits: { S: '#f3e6d0', H: '#ff7a6b', D: '#ff7a6b', C: '#f3e6d0' },
    },
  },
  ember: {
    label: 'Ember',
    back: {
      ground: '#1a0b04',
      pattern: 'repeating-linear-gradient(0deg, rgba(255, 154, 60, 0.28) 0 1px, transparent 1px 5px)',
      backPattern: { kind: 'stripes', ink: 'rgba(255, 154, 60, 0.28)', angle: 0, width: 1, pitch: 5 },
      border: '#ff9a3c',
    },
    face: {
      paper: '#150b06',
      edge: 'rgba(255, 154, 60, 0.5)',
      suits: { S: '#ffe8d0', H: '#ff5a3c', D: '#ff5a3c', C: '#ffe8d0' },
    },
  },
  forest: {
    label: 'Forest',
    back: {
      ground: '#051a10',
      pattern: lattice('rgba(201, 164, 92, 0.3)', '1px', '7px'),
      backPattern: { kind: 'lattice', ink: 'rgba(201, 164, 92, 0.3)', width: 1, pitch: 7 },
      border: '#c9a45c',
    },
    face: {
      paper: '#0b1f15',
      edge: 'rgba(201, 164, 92, 0.5)',
      suits: { S: '#e8f2e0', H: '#ff7a5c', D: '#ff7a5c', C: '#e8f2e0' },
    },
  },
  abyss: {
    label: 'Abyss',
    back: {
      ground: '#050b1c',
      pattern: 'repeating-linear-gradient(90deg, rgba(111, 168, 255, 0.2) 0 2px, transparent 2px 7px)',
      backPattern: { kind: 'stripes', ink: 'rgba(111, 168, 255, 0.2)', angle: 90, width: 2, pitch: 7 },
      border: '#6fa8ff',
    },
    face: {
      paper: '#0a1226',
      edge: 'rgba(111, 168, 255, 0.5)',
      suits: { S: '#dbe7ff', H: '#ff6b7f', D: '#ff6b7f', C: '#dbe7ff' },
    },
  },
  amethyst: {
    label: 'Amethyst',
    back: {
      ground: '#1a0826',
      pattern: 'radial-gradient(circle, rgba(217, 166, 255, 0.4) 0 1.5px, transparent 2px) 0 0 / 8px 8px',
      backPattern: { kind: 'dots', ink: 'rgba(217, 166, 255, 0.4)', radius: 1.5, pitch: 8 },
      border: '#d9a6ff',
    },
    face: {
      paper: '#170b22',
      edge: 'rgba(217, 166, 255, 0.5)',
      suits: { S: '#efe2ff', H: '#ff7ab0', D: '#ff7ab0', C: '#efe2ff' },
    },
  },
  eclipse: {
    label: 'Eclipse',
    back: {
      ground: '#0c0c0e',
      pattern:
        'conic-gradient(rgba(245, 239, 226, 0.1) 25%, transparent 0 50%, rgba(245, 239, 226, 0.1) 0 75%, transparent 0) 0 0 / 10px 10px',
      backPattern: { kind: 'checks', ink: 'rgba(245, 239, 226, 0.1)', size: 10 },
      border: '#f5efe2',
    },
    face: {
      paper: '#141416',
      edge: 'rgba(245, 239, 226, 0.4)',
      suits: { S: '#f5efe2', H: '#ff5e5e', D: '#5ea8ff', C: '#6ee07a' },
    },
  },
  sheet: {
    label: 'Sheet music',
    back: {
      ground: '#f4eee0',
      pattern: staff('#1a1712'),
      backPattern: { kind: 'staff', ink: '#1a1712', width: 1, pitch: 5.25, tile: 42 },
      border: '#1a1712',
    },
    face: {
      paper: '#fbf8f0',
      edge: 'rgba(26, 23, 18, 0.2)',
      suits: { S: '#141210', H: '#b3261e', D: '#b3261e', C: '#141210' },
    },
  },
  nocturne: {
    label: 'Nocturne',
    back: {
      ground: '#0e0c16',
      pattern: staff('#e8c37a'),
      backPattern: { kind: 'staff', ink: '#e8c37a', width: 1, pitch: 5.25, tile: 42 },
      border: '#e8c37a',
    },
    face: {
      paper: '#13111b',
      edge: 'rgba(232, 195, 122, 0.5)',
      suits: { S: '#f2ead8', H: '#ff6b6b', D: '#ff6b6b', C: '#f2ead8' },
    },
  },
  rose: {
    label: 'Rosé',
    back: {
      ground: '#e0578f',
      pattern: staff('#fff4f8'),
      backPattern: { kind: 'staff', ink: '#fff4f8', width: 1, pitch: 5.25, tile: 42 },
      border: '#fff4f8',
    },
    face: {
      paper: '#fff4f8',
      edge: 'rgba(224, 87, 143, 0.35)',
      suits: { S: '#2a0f1c', H: '#d0144f', D: '#d0144f', C: '#2a0f1c' },
    },
  },
  neonrose: {
    label: 'Neon rosé',
    back: {
      ground: '#14030c',
      pattern: staff('#ff4fa3'),
      backPattern: { kind: 'staff', ink: '#ff4fa3', width: 1, pitch: 5.25, tile: 42 },
      border: '#ff4fa3',
    },
    face: {
      paper: '#1a0712',
      edge: 'rgba(255, 79, 163, 0.6)',
      suits: { S: '#ffd6ea', H: '#ff4fa3', D: '#ff4fa3', C: '#ffd6ea' },
    },
  },
  neon: {
    label: 'Neon',
    back: {
      ground: '#12041f',
      pattern: lattice('rgba(255, 111, 176, 0.35)', '1px', '7px'),
      backPattern: { kind: 'lattice', ink: 'rgba(255, 111, 176, 0.35)', width: 1, pitch: 7 },
      border: '#4fe3f0',
    },
    face: {
      paper: '#140a24',
      edge: 'rgba(79, 227, 240, 0.6)',
      suits: { S: '#4fe3f0', H: '#ff6fb0', D: '#ff6fb0', C: '#4fe3f0' },
    },
  },
  hacker: {
    label: 'Hacker',
    back: {
      ground: '#020503',
      pattern: 'repeating-linear-gradient(0deg, rgba(158, 224, 122, 0.3) 0 1px, transparent 1px 4px)',
      backPattern: { kind: 'stripes', ink: 'rgba(158, 224, 122, 0.3)', angle: 0, width: 1, pitch: 4 },
      border: '#9ee07a',
    },
    face: {
      paper: '#06110a',
      edge: 'rgba(158, 224, 122, 0.6)',
      suits: { S: '#9ee07a', H: '#ffb04d', D: '#ffb04d', C: '#9ee07a' },
    },
  },
};

export interface CasinoTheme {
  felt: FeltId;
  chips: ChipSetId;
  deck: DeckId;
}

/** Every casino opens on the green, the chips and the cards it always had. */
export const DEFAULT_THEME: CasinoTheme = { felt: 'emerald', chips: 'classic', deck: 'house' };

export function isFeltId(value: unknown): value is FeltId {
  return typeof value === 'string' && (FELT_IDS as readonly string[]).includes(value);
}

export function isChipSetId(value: unknown): value is ChipSetId {
  return typeof value === 'string' && (CHIP_SET_IDS as readonly string[]).includes(value);
}

export function isDeckId(value: unknown): value is DeckId {
  return typeof value === 'string' && (DECK_IDS as readonly string[]).includes(value);
}

/**
 * A stored or received theme, read leniently: each part that is missing or
 * unknown falls back to the default rather than failing the screen that asked.
 */
export function readTheme(raw: unknown): CasinoTheme {
  const stored = (raw ?? {}) as { felt?: unknown; chips?: unknown; deck?: unknown };
  return {
    felt: isFeltId(stored.felt) ? stored.felt : DEFAULT_THEME.felt,
    chips: isChipSetId(stored.chips) ? stored.chips : DEFAULT_THEME.chips,
    deck: isDeckId(stored.deck) ? stored.deck : DEFAULT_THEME.deck,
  };
}
